export type THttpConfig = {
  apiKey: string;
  baseURL: string;
};

export type THttpErrorData = {
  errors?: Record<string, string[]>;
  message?: string;
};

export class HttpError extends Error {
  constructor(
    public status: number,
    public data?: THttpErrorData,
  ) {
    super(data?.message ?? `Request failed with status ${status}`);
    this.name = 'HttpError';
  }
}

const config: THttpConfig = {
  apiKey: '',
  baseURL: '',
};

export const initHttpAPI = (apiKey: string, apiUrl: string) => {
  config.apiKey = apiKey;
  config.baseURL = `${apiUrl}api/v1/`;
};

export const getHttpConfig = (): THttpConfig => ({ ...config });

export const getHttpHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  'X-Api-Key': config.apiKey,
  'X-Key-Inflection': 'camel',
});

export const getHttpErrorData = async (
  response: Response,
): Promise<THttpErrorData | undefined> => {
  try {
    return (await response.json()) as THttpErrorData;
  } catch {
    return undefined;
  }
};

export const getHttpErrorMessage = (error: unknown): string | undefined =>
  error instanceof HttpError && error.status < 500
    ? error.data?.message
    : undefined;

export const hasHttpFieldError = (error: unknown, field: string): boolean =>
  error instanceof HttpError && !!error.data?.errors?.[field];

const get = async <T>(url: string): Promise<{ data: T; status: number }> => {
  const response = await fetch(`${config.baseURL}${url}`, {
    headers: { ...getHttpHeaders(), Accept: 'application/json' },
    method: 'GET',
  });

  if (!response.ok) {
    throw new HttpError(response.status, await getHttpErrorData(response));
  }

  return { data: (await response.json()) as T, status: response.status };
};

const httpRequest = { get };

export default httpRequest;
